import React, { useMemo } from 'react';
import { Paper, Stack, Typography, Skeleton } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LabelTooltip from '@/shared/components/LabelTooltip';
import { StyledIBMTypography } from '@/shared/orderTable/util';
import { msAndKs } from '@/util';

export default function LifetimeStats({ lifetime }) {
  const lifetimeStats = useMemo(() => {
    const totalVolume = parseFloat(lifetime?.executed_notional || 0);
    const totalFees = parseFloat(lifetime?.fees || 0);

    // Net PnL = sum of all net fees (same as total fees)
    const netPnL = totalFees;
    const netPnLPercentage = totalVolume > 0 ? (netPnL / totalVolume) * 100 : 0;

    return {
      netPnL,
      netPnLPercentage,
      totalVolume,
    };
  }, [lifetime]);

  const netFeesColor = useMemo(() => {
    if (lifetimeStats.netPnL === 0) return 'text.secondary';
    return lifetimeStats.netPnL >= 0 ? 'text.primary' : 'success.main';
  }, [lifetimeStats.netPnL]);

  return (
    <Paper elevation={1} sx={{ pt: 1, pb: 1, px: 2, backgroundColor: 'transparent', backdropFilter: 'blur(5px)' }}>
      <Stack direction='column' spacing={1}>
        <Stack alignItems='center' direction='row' spacing={1}>
          <SmartToyIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant='body1'>Lifetime Summary</Typography>
        </Stack>

        <Stack direction='column' spacing={1}>
          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Volume'
              link='https://docs.tread.fi/market-maker-bot#lifetime-summary'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>
                    Total executed volume across all Volume Bot orders.
                  </Typography>
                </div>
              }
            />
            <StyledIBMTypography
              color={lifetimeStats.totalVolume > 0 ? 'text.primary' : 'text.secondary'}
              style={{ display: 'inline' }}
              variant='body2'
            >
              {lifetimeStats.totalVolume > 0 ? `$${msAndKs(lifetimeStats.totalVolume, 2)}` : '-'}
            </StyledIBMTypography>
          </Stack>

          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Net Fees'
              link='https://docs.tread.fi/market-maker-bot#lifetime-summary'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>Sum of all net fees across all Volume Bot orders.</Typography>
                </div>
              }
            />
            <StyledIBMTypography color={netFeesColor} style={{ display: 'inline' }} variant='body2'>
              {lifetimeStats.netPnL !== 0 ? `$${msAndKs(lifetimeStats.netPnL, 2)}` : '-'}
            </StyledIBMTypography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
